import { CallExpressionShape, type ObjectShape, type Plugin, type Shape } from '@servicenow/sdk-build-core'
import { type ArrowFunctionShape, NowIdShape } from '@servicenow/sdk-build-plugins'
import type { Record as SdkRecord, Diagnostics, Transform } from '@servicenow/sdk-build-core'
import { parseArrowBodyEntries } from '../utils/arrow-body-parser'

export interface ChildCollectionTypeConfig {
    createShape: (
        source: Shape,
        config: ObjectShape,
        context?: { nowIdKeyToGuid?: Map<string, string> }
    ) => CallExpressionShape
    childPlugin: Plugin
    configArgIndex?: number
}

export interface ChildCollectionToRecordConfig {
    /** The arrow function containing the child collection (e.g. the lanes or activities arrow) */
    collectionArrow: ArrowFunctionShape
    /** Parent record (used for FK value) */
    parentRecord: SdkRecord
    /** Field name on child records that references the parent */
    parentFkField: string
    /** Optional valid callee names for child call expressions (e.g. ['Lane', 'wfa.playbook.lane']) */
    calleeNames?: string[]
    /**
     * Per-callee dispatch map for heterogeneous collections.
     * Maps callee name → type-specific config (plugin, shape factory, etc.).
     */
    childTypeMap: Map<string, ChildCollectionTypeConfig>
    /** Optional hook to resolve cross-references between siblings */
    resolveReferences?: (
        childConfig: ObjectShape,
        variables: Map<string, CallExpressionShape>,
        nowIdKeyToGuid: Map<string, string>
    ) => string | undefined
    /** Field name for resolved references on the child config (e.g. 'startsAfterLanes') */
    resolvedRefField?: string
    /** Optional hook to resolve branch references (PropertyAccessShape args in Run.After) */
    resolveBranchReferences?: (
        childConfig: ObjectShape,
        variables: Map<string, CallExpressionShape>,
        nowIdKeyToGuid: Map<string, string>,
        branchIdMap: Map<string, Map<string, string>>
    ) => string | undefined
    /** Field name for resolved branch references (e.g. 'startsAfterBranches') */
    resolvedBranchRefField?: string
    /**
     * When set, auto-derive activity sys_ids from resolved branch references
     * (format: <activitySysId>.<branchId>) and merge them into this field.
     * Used for lanes in stage-level decisions where the platform requires both
     * starts_after_activities and starts_after_branches.
     */
    resolvedBranchActivityField?: string
    /**
     * Optional callback to extract branch ID mappings from a processed Decision activity.
     * Called after each successful toRecord. Returns a map of fluentBranchId → generatedGuid
     * for the given activity, or undefined if the activity is not a Decision.
     */
    extractBranchIdMap?: (callExpr: CallExpressionShape, resultRecord: SdkRecord) => Map<string, string> | undefined
    /**
     * Optional callback to compute per-child merge props before toRecord.
     * Receives all child configs, the current index, and the callee name of
     * each child (so callers can branch on child type, e.g. activity vs lane).
     * Returns extra fields to merge. Useful for computing unique names across siblings.
     */
    computePerChildMergeProps?: (
        allChildConfigs: ObjectShape[],
        index: number,
        allChildCallees: string[]
    ) => Record<string, string>
    /**
     * Optional validation hook called after all children are parsed but before records are created.
     * Receives the full children array and the variable map so cross-child analysis (e.g. redundant
     * edge detection) can be performed and diagnostics emitted.
     */
    validateChildren?: (
        children: Array<{ callExpr: CallExpressionShape; childConfig: ObjectShape }>,
        variables: Map<string, CallExpressionShape>
    ) => void
    /** Optional external Now.ID→GUID map. If provided, entries are added to this map
     *  instead of creating a new one. Enables sharing across multiple calls. */
    externalNowIdKeyToGuid?: Map<string, string> | undefined
    /** Optional external branch ID map. If provided, entries are added to this map
     *  instead of creating a new one. Enables sharing across multiple calls. */
    externalBranchIdMap?: Map<string, Map<string, string>> | undefined
    /** Build transform handle */
    transform: Transform
    /** Diagnostics handle */
    diagnostics: Diagnostics
    /** Error message for missing return object */
    errorMessage: string
    /** The call expression source for error reporting */
    errorSource: Shape
}

/**
 * Process a child collection from a body arrow function into records.
 *
 * Handles: arrow parsing, identifier resolution, callee matching,
 * optional config key nesting, reference resolution, parent FK merging,
 * and Now.ID → GUID tracking.
 */
export async function processChildCollectionToRecord(config: ChildCollectionToRecordConfig): Promise<SdkRecord[]> {
    const {
        collectionArrow,
        parentRecord,
        parentFkField,
        calleeNames,
        childTypeMap,
        resolveReferences,
        resolvedRefField,
        resolveBranchReferences,
        resolvedBranchRefField,
        resolvedBranchActivityField,
        extractBranchIdMap,
        computePerChildMergeProps,
        validateChildren,
        externalNowIdKeyToGuid,
        externalBranchIdMap,
        transform,
        diagnostics,
        errorMessage,
        errorSource,
    } = config

    const { variables, returnObj } = parseArrowBodyEntries(collectionArrow)

    if (!returnObj) {
        diagnostics.error(errorSource, errorMessage)
        return []
    }

    if (!returnObj.isObject()) {
        return []
    }

    const returnObjEntries = returnObj.asObject()
    const nowIdKeyToGuid = externalNowIdKeyToGuid ?? new Map<string, string>()
    const records: SdkRecord[] = []
    const allowedCalleeNames = calleeNames ?? [...childTypeMap.keys()]

    // First pass: collect resolved child configs and call expressions
    const children: Array<{
        callExpr: CallExpressionShape
        childConfig: ObjectShape
        typeConfig: ChildCollectionTypeConfig
        callee: string
    }> = []
    for (const [, childValue] of returnObjEntries.entries()) {
        // Resolve identifier references (e.g. lane_0) to their call expression
        const resolved = childValue.isIdentifier()
            ? (variables.get(childValue.asIdentifier().getName()) ?? childValue)
            : childValue

        if (!resolved.is(CallExpressionShape)) {
            continue
        }

        const callExpr = resolved.as(CallExpressionShape)
        const callee = callExpr.getCallee()
        if (!allowedCalleeNames.includes(callee)) {
            continue
        }

        const typeConfig = childTypeMap.get(callee)
        if (!typeConfig) {
            continue
        }

        const argIndex = typeConfig.configArgIndex ?? 0
        const callArg = callExpr.getArgument(argIndex).asObject()
        const configShape = callArg.get('config')
        const childConfig = configShape?.isUndefined() === false ? configShape.asObject() : callArg

        children.push({ callExpr, childConfig, typeConfig, callee })
    }

    // Extract all child configs and callees for per-child merge props computation
    const allChildConfigs = children.map((c) => c.childConfig)
    const allChildCallees = children.map((c) => c.callee)

    // Run cross-child validation (e.g. redundant edge detection) before creating records
    if (validateChildren) {
        validateChildren(children, variables)
    }

    const branchIdMap = externalBranchIdMap ?? new Map<string, Map<string, string>>()

    // Second pass: create records
    for (const [index, { callExpr, childConfig, typeConfig }] of children.entries()) {
        const createShape = typeConfig.createShape
        const childPlugin = typeConfig.childPlugin

        // Resolve cross-references if hook provided
        const mergeProps: Record<string, string> = {
            [parentFkField]: parentRecord.getId().getValue(),
        }
        if (resolveReferences && resolvedRefField) {
            const resolvedRef = resolveReferences(childConfig, variables, nowIdKeyToGuid)
            if (resolvedRef !== undefined) {
                mergeProps[resolvedRefField] = resolvedRef
            }
        }
        if (resolveBranchReferences && resolvedBranchRefField) {
            const resolvedBranch = resolveBranchReferences(childConfig, variables, nowIdKeyToGuid, branchIdMap)
            if (resolvedBranch !== undefined) {
                mergeProps[resolvedBranchRefField] = resolvedBranch
                // Auto-derive activity sys_ids from branch refs (format: <sysId>.<branchId>)
                if (resolvedBranchActivityField) {
                    const actSysIds = [
                        ...new Set(
                            resolvedBranch
                                .split(',')
                                .map((ref) => ref.split('.')[0])
                                .filter(Boolean)
                        ),
                    ]
                    if (actSysIds.length > 0) {
                        mergeProps[resolvedBranchActivityField] = actSysIds.join(',')
                    }
                }
            }
        }
        if (computePerChildMergeProps) {
            Object.assign(mergeProps, computePerChildMergeProps(allChildConfigs, index, allChildCallees))
        }

        const configWithParent = childConfig.merge(mergeProps)
        const shape = createShape(callExpr, configWithParent, { nowIdKeyToGuid })
        const result = await transform.toRecord(shape, childPlugin)

        if (result.success) {
            // Track Now.ID key → GUID for sibling cross-references
            const idShape = childConfig.get('$id')
            if (idShape instanceof NowIdShape) {
                nowIdKeyToGuid.set(String(idShape.getValue()), result.value.getId().getValue())
            }
            // Extract branch ID mappings for Decision activities
            if (extractBranchIdMap) {
                const mapping = extractBranchIdMap(callExpr, result.value)
                if (mapping) {
                    branchIdMap.set(result.value.getId().getValue(), mapping)
                }
            }
            records.push(result.value)
        }
    }

    return records
}
